import { AiIdeaType, AiIdea } from './aiConversationTypes';

/**
 *
 * @param idea a TIdea to convert to structured text
 *
 * text is in format of:
 *
 * ```
 * type: idea
 * text: text
 * more text, but without a key parameter
 * ```
 */
export const convertIdeaToStructuredText = (idea: AiIdea): string => {
    switch (idea.type) {
        case AiIdeaType.COLOR:
            return `type: ${idea.type}
hex: ${idea.hex}
text: ${idea.text}`;
        default:
            return `type: ${idea.type}
text: ${idea.text}`;
    }
};

export const convertIdeasToStructuredText = (ideas: AiIdea[]): string => {
    const rows = ideas.map(convertIdeaToStructuredText);
    return rows.join(`\n###\n`);
};

/**
 *
 * @param text a string of text to parse into a TIdea
 * @returns TIdea
 *
 * text is in format of:
 *
 * ```
 * type: idea
 * text: text
 * more text, but without a key parameter
 * ```
 * or
 *
 * ````
 * type: color
 * hex: #ffffff
 * text: text
 * ```
 */

const getIdeaTypeFromText = (text: string): AiIdeaType => {
    const trimmedText = text.trim().toLowerCase();

    switch (trimmedText) {
        case 'color':
            return AiIdeaType.COLOR;
        case 'task':
            return AiIdeaType.TASK;
        case 'idea':
            return AiIdeaType.IDEA;
        default:
            return AiIdeaType.OTHER;
    }
};

const isMarkdownList = (line: string): boolean => {
    return line.match(/^[-*]\s|\d+\./) !== null;
};

const isCodeBlockContext = (text: string): boolean => {
    return (text.match(/```/g) || []).length % 2 === 1;
};

const appendTextLine = (text: string, line: string): string => {
    // if the line is a markdown list, use a single newline
    if (isMarkdownList(line)) return `${text}\n${line}`;

    // in code block context, use a single newline
    if (isCodeBlockContext(text)) return `${text}\n${line}`;

    // otherwise, use a double newline
    return `${text}\n\n${line}`;
};

const AI_PROMPT_COMPLETION_FAILURE_REGEX = new RegExp(
    '^(' +
        // I'm sorry... I apologize... ethical, moral...
        "(I|I'm)\\s.*(sorry|apologize|ethical|moral)|" +
        // Sorry I'm an AI... Artificial Intelligence...
        'sorry.*(AI\\s|artificial intelligence|language model)|' +
        // As an artificial intelligence language model...
        'as\\san\\s(AI\\s|artificial intelligence|language model)|' +
        // I cannot provide instructions... illegal... unethical
        '.*I\\scannot.*(illegal|unethical)' +
        ')',
    'i',
);

/**
 * Ensures that ideas with a type of "other" are actually conversational messages.
 * Otherwise, converts them to "idea" objects so that they're treated like normal results.
 */
const validateOrConvertConversationalIdeas = (idea: AiIdea) => {
    if (idea.type !== AiIdeaType.OTHER) return idea;

    // If the text matches the conversational regex, then leave it as a type of "other"
    if (!idea.text || idea.text.match(AI_PROMPT_COMPLETION_FAILURE_REGEX)) return idea;

    return {
        ...idea,
        type: AiIdeaType.IDEA,
    };
};

export const parseStructuredTextIdea = (text: string): AiIdea => {
    const lines = text.split('\n');

    const idea = lines.reduce(
        (idea, line) => {
            const trimmedLine = line.trim();

            if (!trimmedLine.length) return idea;
            if (trimmedLine.startsWith('###')) return idea;

            // if the line doesn't have a colon, then it's part of the text
            if (line.indexOf(':') === -1) {
                idea.text = appendTextLine(idea.text, line);
                return idea;
            }

            // otherwise, it's a key/value pair
            const [key, value] = line.split(/:(.*)/); // split on the first colon

            const trimmedKey: string = key.trim().toLowerCase();
            const trimmedValue: string = value.trim();

            switch (trimmedKey) {
                case 'type':
                    idea.type = getIdeaTypeFromText(trimmedValue);
                    break;
                case 'text':
                    idea.text = `${idea.text}\n${trimmedValue}`;
                    break;
                case 'hex':
                    idea.hex = trimmedValue;
                    break;
                default:
                    idea.text = appendTextLine(idea.text, line);
                    break;
            }

            return idea;
        },
        {
            // Default to "other" so that these messages will be validated by the "conversational" check
            // before being returned as an "idea"
            type: AiIdeaType.OTHER,
            text: '',
        } as AiIdea,
    );

    idea.text = idea.text.trim();

    // Finally ensure that any idea with a type of "other" is conversational
    return validateOrConvertConversationalIdeas(idea);
};

export const parseStructuredTextIdeas = (text: string): AiIdea[] => {
    // split the text into chunks, each chunk is a separate idea
    const lines = text.split('\n');
    const ideaChunks = lines
        .reduce(
            (chunks: string[], line) => {
                const trimmedLine = line.trim().toLowerCase();

                const currentChunk = chunks[chunks.length - 1];

                // if the line starts with ###, start a new chunk
                if (trimmedLine.startsWith('###')) return [...chunks, ''];

                // if the line contains a `type:`, but the current chunk already has a `type:`, start a new chunk
                if (trimmedLine.includes('type:') && currentChunk.includes('type:')) return [...chunks, line];

                return [...chunks.slice(0, -1), `${currentChunk}\n${line}`];
            },
            [''],
        )
        .filter((chunk) => chunk.trim().length);

    return ideaChunks.map(parseStructuredTextIdea);
};
